/**
 * *Lock view to restrict edits* (`specs/M4-V1-views.md` §2.3).
 *
 * Measured: a **482 × 159** modal reading "Lock the <name> view" / "Only board owners will be
 * able to edit this view." / *Cancel* · *Lock view* `[cap:sandbox-lock-view.png]`. That is
 * monday's entire per-view permission model — there is no per-user or per-team ACL.
 */
import { Button, useOverlayDismiss } from '@elizmonday/ui';
import { useEffect, useRef } from 'react';

export function LockViewDialog({
  name,
  locked,
  onConfirm,
  onClose,
}: {
  name: string;
  locked: boolean;
  onConfirm(): void;
  onClose(): void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /**
   * **P-260.** The bespoke Escape listener closed the dialog and left focus on `<body>`; the one
   * hook closes it *and* puts focus back on the `Lock view` row — which, because the tab menu
   * unmounted in the commit that mounted this, means the tab's `···` the menu itself came from.
   */
  useOverlayDismiss({ onClose });

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
  }, []);

  return (
    <div className="ez-view-modal__scrim" data-testid="lock-view-scrim" onClick={onClose}>
      <div
        ref={ref}
        className="ez-view-modal"
        role="dialog"
        aria-modal="true"
        aria-label={locked ? `Unlock the ${name} view` : `Lock the ${name} view`}
        data-testid="lock-view-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="ez-view-modal__title">
          {locked ? `Unlock the ${name} view` : `Lock the ${name} view`}
        </h2>
        <p className="ez-view-modal__body">
          {locked
            ? 'Everyone with edit access will be able to change this view again.'
            : 'Only board owners will be able to edit this view.'}
        </p>
        <div className="ez-view-modal__footer">
          <Button kind="tertiary" onClick={onClose} data-testid="lock-view-cancel">
            Cancel
          </Button>
          <Button onClick={onConfirm} data-testid="lock-view-confirm">
            {locked ? 'Unlock view' : 'Lock view'}
          </Button>
        </div>
      </div>
    </div>
  );
}
